import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDiscount2, IconEdit, IconPlus, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useDeletePromotion, usePromotions } from './promotionsApi';
import { PromotionFormDrawer } from './PromotionFormDrawer';
import { PROMOTION_TYPE, type PromotionDto } from './types';

export function PromotionsPage() {
  const { t } = useTranslation();
  const { data: rows, isLoading } = usePromotions();
  const deleteMutation = useDeletePromotion();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<PromotionDto | null>(null);
  const [deleting, setDeleting] = useState<PromotionDto | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const openEdit = (p: PromotionDto) => {
    setEditing(p);
    setDrawerOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await deleteMutation.mutateAsync(deleting.promotionId);
      notifications.show({
        color: 'green',
        title: t('common.toasts.deleted'),
        message: deleting.code,
      });
      setDeleting(null);
    } catch {
      notifications.show({
        color: 'red',
        title: t('common.toasts.errorTitle'),
        message: t('common.toasts.errorBody'),
      });
    }
  };

  const hasPromotions = (rows?.length ?? 0) > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.catalog')}
        title={t('promotions.page.title')}
        subtitle={t('promotions.page.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>
            {t('promotions.page.create')}
          </Button>
        }
      />

      <Paper p={hasPromotions ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}>
            <Loader size="sm" />
          </Stack>
        ) : !hasPromotions ? (
          <EmptyState
            icon={<IconDiscount2 size={22} stroke={1.6} />}
            title={t('promotions.empty.title')}
            description={t('promotions.empty.body')}
            actionLabel={t('promotions.page.create')}
            onAction={openCreate}
          />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('promotions.columns.code')}</Table.Th>
                <Table.Th>{t('promotions.columns.nameAr')}</Table.Th>
                <Table.Th>{t('promotions.columns.percent')}</Table.Th>
                <Table.Th>{t('promotions.columns.window')}</Table.Th>
                <Table.Th>{t('promotions.columns.status')}</Table.Th>
                <Table.Th style={{ width: 100, textAlign: 'end' }}>
                  {t('common.actions')}
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows!.map((p) => (
                <Table.Tr key={p.promotionId}>
                  <Table.Td>
                    <span className="font-mono text-sm text-[var(--wheels-ink-strong)]">
                      {p.code}
                    </span>
                  </Table.Td>
                  <Table.Td fw={500}>{p.nameAr}</Table.Td>
                  <Table.Td className="font-numeric">{renderRule(p)}</Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)" fz="xs">
                    {renderWindow(p, t)}
                  </Table.Td>
                  <Table.Td>
                    {p.isActive ? (
                      <Badge color="green" variant="light" radius="sm">
                        {t('common.active')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" radius="sm">
                        {t('common.inactive')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <Tooltip label={t('common.edit')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          onClick={() => openEdit(p)}
                          aria-label={t('common.edit')}
                        >
                          <IconEdit size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          onClick={() => setDeleting(p)}
                          aria-label={t('common.delete')}
                        >
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <PromotionFormDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        promotion={editing}
      />

      <ConfirmDialog
        opened={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={t('promotions.delete.title')}
        message={t('promotions.delete.body', { code: deleting?.code ?? '' })}
        loading={deleteMutation.isPending}
      />
    </Stack>
  );
}

function renderWindow(p: PromotionDto, t: (k: string) => string): string {
  const fmt = (s: string) => dayjs(s).format('YYYY-MM-DD HH:mm');
  if (!p.activeFrom && !p.activeTo) return t('promotions.window.always');
  if (p.activeFrom && p.activeTo) return `${fmt(p.activeFrom)} → ${fmt(p.activeTo)}`;
  if (p.activeFrom) return `${t('promotions.window.from')} ${fmt(p.activeFrom)}`;
  return `${t('promotions.window.until')} ${fmt(p.activeTo!)}`;
}

function renderRule(p: PromotionDto): string {
  if (p.type === PROMOTION_TYPE.BOGO_SAME_ITEM) {
    return `${p.bogoBuyQty}+${p.bogoFreeQty}`;
  }
  if (p.type === PROMOTION_TYPE.AMOUNT_OFF_ORDER) {
    return `₪ ${p.discountAmount.toFixed(2)}`;
  }
  return `${p.discountPercent.toFixed(0)}%`;
}
